import { useParams, useNavigate, Link as RouterLink } from "react-router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import DashboardLayout from "../layouts/dashboard";
import { StaggerContainer, StaggerItem } from "../../interface/shared/animations";
import { useStockRelocationDetail } from "../../api/stockrelocation";
import { ExpiresAtDisplay } from "../../interface/shared/expirydisplay";
import { WeightDisplay } from "../../interface/shared/weightdisplay";
import { StockRelocationRequest } from "../../shared/types";
import { stockLocationDisplayName } from "../../shared/stock-location-display-name";

export default function StockRelocationDetails() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { relocation, errors, loading, fetchRelocation } = useStockRelocationDetail(id);

    return (
        <DashboardLayout>
            <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                    <Button
                        startIcon={<ArrowBackRoundedIcon />}
                        onClick={() => navigate("/relocations")}
                    >
                        Back
                    </Button>
                </Stack>

                {errors.length > 0 && (
                    <Alert
                        severity="error"
                        sx={{ mb: 2 }}
                        action={
                            <Button color="inherit" size="small" onClick={() => fetchRelocation()}>
                                Retry
                            </Button>
                        }
                    >
                        {errors.join(" ")}
                    </Alert>
                )}

                {loading ? <_DetailsSkeleton /> : relocation && (
                    <StaggerContainer>
                        <StaggerItem>
                            <_RelocationSummary relocation={relocation} />
                        </StaggerItem>

                        <StaggerItem>
                            <Typography variant="h6" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
                                Source Stock
                            </Typography>
                            <_StockCard relocation={relocation} />
                        </StaggerItem>

                        {relocation.location && (
                            <StaggerItem>
                                <Typography variant="h6" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
                                    Target Location
                                </Typography>
                                <_LocationCard relocation={relocation} />
                            </StaggerItem>
                        )}
                    </StaggerContainer>
                )}
            </Box>
        </DashboardLayout>
    );
}

function _statusColor(status: string): "default" | "success" | "warning" | "error" | "info" {
    switch (status) {
        case "completed": return "success";
        case "pending": return "warning";
        case "cancelled": return "error";
        default: return "default";
    }
}

function _RelocationSummary({ relocation }: { relocation: StockRelocationRequest }) {
    return (
        <Card variant="outlined">
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                    <Box>
                        <Typography variant="h6">
                            Relocation Request
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {relocation.product_stock.product.code} &middot; {relocation.product_stock.product.name}
                        </Typography>
                    </Box>
                </Stack>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">From Location</Typography>
                        <Typography variant="body1" fontWeight={500}>
                            {relocation.product_stock.location.name}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">From Position</Typography>
                        <Typography variant="body1" fontWeight={500}>{stockLocationDisplayName(relocation.product_stock.location, relocation.product_stock.level, relocation.product_stock.column)}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">To Location</Typography>
                        <Typography variant="body1" fontWeight={500}>{relocation.location ? (
                            <>{relocation.location.name} ({relocation.location.warehouse.name})</>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                Into stock
                            </Typography>
                        )}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">To Position</Typography>
                        <Typography variant="body1" fontWeight={500}>{relocation.location ? stockLocationDisplayName(relocation.location, relocation.level, relocation.column) : '-'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Split pallet</Typography>
                        <Typography variant="body1" fontWeight={500}>{relocation.split_pallet ? 'Yes' : 'No'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Requested By</Typography>
                        <Typography variant="body1" fontWeight={500}>{relocation.user.name}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Requested At</Typography>
                        <Typography variant="body1" fontWeight={500}>
                            {relocation.created_at.toLocaleDateString()} {relocation.created_at.toLocaleTimeString()}
                        </Typography>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}

function _StockCard({ relocation }: { relocation: StockRelocationRequest }) {
    const stock = relocation.product_stock;

    return (
        <Card variant="outlined">
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                    <Box>
                        <Link
                            component={RouterLink}
                            to={`/stock/${stock.id}`}
                            variant="h6"
                            underline="hover"
                        >
                            {stock.product.code} &middot; {stock.location.name}
                        </Link>
                        <Typography variant="body2" color="text.secondary">
                            {stock.product.name}
                        </Typography>
                    </Box>
                </Stack>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 6, sm: 2 }}>
                        <Typography variant="caption" color="text.secondary">Warehouse</Typography>
                        <Typography variant="body1" fontWeight={500}>{stock.location.warehouse.name}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                        <Typography variant="caption" color="text.secondary">Location</Typography>
                        <Typography variant="body1" fontWeight={500}>{stock.location.name}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                        <Typography variant="caption" color="text.secondary">Position</Typography>
                        <Typography variant="body1" fontWeight={500}>{stockLocationDisplayName(stock.location, stock.level, stock.column)}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                        <Typography variant="caption" color="text.secondary">Quantity</Typography>
                        <Typography variant="body1" fontWeight={500}>{stock.quantity}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                        <Typography variant="caption" color="text.secondary">Weight</Typography>
                        <Typography variant="body1" fontWeight={500}><WeightDisplay value={stock.weight} /></Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                        <Typography variant="caption" color="text.secondary">Expires</Typography>
                        <Typography variant="body1" fontWeight={500}>
                            <ExpiresAtDisplay value={stock.expires_at} />
                        </Typography>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}

function _LocationCard({ relocation }: { relocation: StockRelocationRequest }) {
    const location = relocation.location;

    return (
        <Card variant="outlined">
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                    <Box>
                        <Typography variant="h6">
                            {location ? (
                                <>{location.name} ({location.warehouse.name})</>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    Into stock
                                </Typography>
                            )}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {location ? location.warehouse.name : '-'}
                        </Typography>
                    </Box>
                </Stack>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Location</Typography>
                        <Typography variant="body1" fontWeight={500}>{location ? location.name : '-'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Warehouse</Typography>
                        <Typography variant="body1" fontWeight={500}>{location ? location.warehouse.name : '-'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Target Position</Typography>
                        <Typography variant="body1" fontWeight={500}>{location ? stockLocationDisplayName(location, relocation.level, relocation.column) : '-'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Total Levels</Typography>
                        <Typography variant="body1" fontWeight={500}>{location ? location.levels : '-'}</Typography>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}

function _DetailsSkeleton() {
    return (
        <>
            <Card variant="outlined">
                <CardContent>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 3 }}>
                        <Box>
                            <Skeleton variant="text" width={200} height={32} />
                            <Skeleton variant="text" width={280} height={20} />
                        </Box>
                        <Skeleton variant="rounded" width={70} height={24} />
                    </Stack>
                    <Grid container spacing={3}>
                        {Array.from({ length: 6 }).map((_, i) => (
                            <Grid key={i} size={{ xs: 6, sm: 3 }}>
                                <Skeleton variant="text" width={80} height={16} />
                                <Skeleton variant="text" width={100} height={24} />
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>
            <Skeleton variant="text" width={140} height={28} sx={{ mt: 4, mb: 2 }} />
            <Card variant="outlined">
                <CardContent>
                    <Skeleton variant="text" width={200} height={32} />
                    <Skeleton variant="text" width={150} height={20} sx={{ mb: 3 }} />
                    <Grid container spacing={3}>
                        {Array.from({ length: 6 }).map((_, i) => (
                            <Grid key={i} size={{ xs: 6, sm: 2 }}>
                                <Skeleton variant="text" width={80} height={16} />
                                <Skeleton variant="text" width={100} height={24} />
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>
            <Skeleton variant="text" width={140} height={28} sx={{ mt: 4, mb: 2 }} />
            <Card variant="outlined">
                <CardContent>
                    <Skeleton variant="text" width={200} height={32} />
                    <Skeleton variant="text" width={150} height={20} sx={{ mb: 3 }} />
                    <Grid container spacing={3}>
                        {Array.from({ length: 4 }).map((_, i) => (
                            <Grid key={i} size={{ xs: 6, sm: 3 }}>
                                <Skeleton variant="text" width={80} height={16} />
                                <Skeleton variant="text" width={100} height={24} />
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>
        </>
    );
}
